import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import apiClient from '../../utils/apiClient';
import {
  removePendingActivationByEmail,
  removePendingActivationBySessionId,
  setPendingActivationSession,
} from '../../utils/activationSessionStore';
import styles from './SignupActivation.module.css';

const DEFAULT_COOLDOWN_SECONDS = 60;

const parseApiMessage = (error, fallbackMessage) => {
  const data = error?.response?.data;
  return (
    data?.message ||
    data?.error ||
    data?.detail ||
    (typeof data === 'string' ? data : null) ||
    fallbackMessage
  );
};

const SignupActivation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const token = String(searchParams.get('token') || '').trim();
  const sessionFromQuery = String(searchParams.get('activation_session_id') || '').trim();

  const [activationSessionId, setActivationSessionId] = useState(sessionFromQuery);
  const [prefillEmail] = useState(location.state?.prefillEmail || '');
  const [maskedEmail, setMaskedEmail] = useState(location.state?.maskedEmail || '');
  const [maskedPhone, setMaskedPhone] = useState(location.state?.maskedPhone || '');
  const [smsCode, setSmsCode] = useState('');
  const [showPhoneEdit, setShowPhoneEdit] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [status, setStatus] = useState({
    email_verified: false,
    phone_verified: false,
    requires_email: true,
    requires_phone: false,
    is_complete: false,
    is_finalized: false,
    email_delivery_status: null,
    portal_terms_required: false,
  });
  const [termsCatalog, setTermsCatalog] = useState([]);
  const [openTermsDoc, setOpenTermsDoc] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  /** Requête HTTP de renvoi email réellement en vol (≠ cooldown). */
  const [emailSending, setEmailSending] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  /** Le serveur a accepté au moins un envoi (2xx / file d'attente). */
  const [activationEmailAccepted, setActivationEmailAccepted] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [debugActivationLink, setDebugActivationLink] = useState('');
  const [debugSmsCode, setDebugSmsCode] = useState('');
  const [emailCooldown, setEmailCooldown] = useState(0);
  const [smsCooldown, setSmsCooldown] = useState(0);

  useEffect(() => {
    if (!emailCooldown && !smsCooldown) return undefined;
    const timer = setInterval(() => {
      setEmailCooldown((prev) => (prev > 0 ? prev - 1 : 0));
      setSmsCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [emailCooldown, smsCooldown]);

  const updateStatus = (newStatus) => {
    if (!newStatus || typeof newStatus !== 'object') return;
    setStatus({
      email_verified: Boolean(newStatus.email_verified),
      phone_verified: Boolean(newStatus.phone_verified),
      requires_email:
        newStatus.requires_email == null ? true : Boolean(newStatus.requires_email),
      requires_phone:
        newStatus.requires_phone == null ? false : Boolean(newStatus.requires_phone),
      is_complete: Boolean(newStatus.is_complete),
      is_finalized: Boolean(newStatus.is_finalized),
      email_delivery_status: newStatus.email_delivery_status || null,
      portal_terms_required: Boolean(newStatus.portal_terms_required),
    });
  };

  const fetchStatus = async (sessionId) => {
    if (!sessionId) return;
    const response = await apiClient.get(
      `/auth/activation/status?activation_session_id=${encodeURIComponent(sessionId)}`
    );
    if (response?.data?.masked_email) {
      setMaskedEmail(response.data.masked_email);
    }
    if (response?.data?.masked_phone) {
      setMaskedPhone(response.data.masked_phone);
    }
    updateStatus(response?.data?.activation_status);
    const retryAfter = Number(response?.data?.email_resend_retry_after_seconds || 0);
    if (retryAfter > 0) {
      setEmailCooldown(retryAfter);
    }
    const delivery = response?.data?.activation_status?.email_delivery_status;
    if (
      delivery === 'queued' ||
      delivery === 'sending' ||
      delivery === 'sent' ||
      delivery === 'delivered'
    ) {
      setActivationEmailAccepted(true);
    }
  };

  useEffect(() => {
    const bootWithEmailToken = async () => {
      if (!token) return;
      setErrorMessage('');
      setInfoMessage('');
      setDebugActivationLink('');
      setDebugSmsCode('');
      setLoading(true);
      try {
        const verifyResponse = await apiClient.post('/auth/activation/verify-email', { token });
        const sessionId = verifyResponse?.data?.activation_session_id;
        if (verifyResponse?.data?.masked_email) {
          setMaskedEmail(verifyResponse.data.masked_email);
        }
        if (verifyResponse?.data?.masked_phone) {
          setMaskedPhone(verifyResponse.data.masked_phone);
        }
        if (sessionId) {
          setActivationSessionId(sessionId);
          // Retirer le jeton de l'URL (historique / Referer)
          navigate(
            `/activate-account?activation_session_id=${encodeURIComponent(sessionId)}`,
            { replace: true, state: location.state }
          );
          await fetchStatus(sessionId);
          setInfoMessage('Email confirmé avec succès.');
        } else {
          setErrorMessage("Email confirmé, mais session d'activation introuvable.");
        }
      } catch (error) {
        const details = error?.response?.data?.details;
        const recoveredSessionId = String(details?.activation_session_id || '').trim();
        const notice = parseApiMessage(
          error,
          "Ce lien n'est plus valide. Utilisez le dernier email reçu ou renvoyez-en un."
        );
        setErrorMessage(notice);
        // Lien remplacé / expiré : on récupère quand même la session pour afficher
        // le bon canal (email seul) et permettre un nouvel envoi.
        if (recoveredSessionId) {
          if (details?.masked_email) {
            setMaskedEmail(details.masked_email);
          }
          if (details?.masked_phone) {
            setMaskedPhone(details.masked_phone);
          }
          if (details?.activation_status) {
            updateStatus(details.activation_status);
          }
          setActivationSessionId(recoveredSessionId);
          navigate(
            `/activate-account?activation_session_id=${encodeURIComponent(recoveredSessionId)}`,
            {
              replace: true,
              state: {
                ...(location.state || {}),
                activationNotice: notice,
                maskedEmail: details?.masked_email || location.state?.maskedEmail,
                maskedPhone: details?.masked_phone || location.state?.maskedPhone,
              },
            }
          );
        }
      } finally {
        setLoading(false);
      }
    };
    bootWithEmailToken();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const bootWithSessionId = async () => {
      if (token) return;
      const noticeFromState = String(location.state?.activationNotice || '').trim();
      setErrorMessage(noticeFromState);
      setInfoMessage('');
      setDebugActivationLink('');
      setDebugSmsCode('');
      if (!activationSessionId) {
        setErrorMessage("Session d'activation introuvable. Recommencez l'inscription.");
        return;
      }
      setLoading(true);
      try {
        await fetchStatus(activationSessionId);
        if (noticeFromState) {
          setErrorMessage(noticeFromState);
        }
      } catch (error) {
        setErrorMessage(parseApiMessage(error, "Impossible de charger la session d'activation."));
      } finally {
        setLoading(false);
      }
    };
    bootWithSessionId();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activationSessionId, token]);

  const markCooldownFromError = (error, setCooldown) => {
    const retryAfter = Number(error?.response?.data?.details?.retry_after_seconds || 0);
    if (retryAfter > 0) {
      setCooldown(retryAfter);
      return retryAfter;
    }
    return 0;
  };

  const isEmailDeliveryInProgressError = (error) => {
    const reason = String(error?.response?.data?.details?.reason || '');
    const message = String(
      error?.response?.data?.message || error?.response?.data?.error || ''
    ).toLowerCase();
    return (
      reason === 'email_delivery_in_progress' ||
      message.includes('envoi déjà') ||
      message.includes('déjà été demandé')
    );
  };

  const recipientLabel = maskedEmail || prefillEmail || 'votre adresse email';

  const handleResendEmail = async () => {
    if (!activationSessionId || emailSending || emailCooldown > 0) return;
    setErrorMessage('');
    setInfoMessage('');
    setDebugActivationLink('');
    setDebugSmsCode('');
    setEmailSending(true);
    try {
      const response = await apiClient.post('/auth/activation/resend-email', {
        activation_session_id: activationSessionId,
      });
      setActivationEmailAccepted(true);
      setEmailCooldown(DEFAULT_COOLDOWN_SECONDS);
      if (response?.data?.activation_status) {
        updateStatus(response.data.activation_status);
      }
      const fallbackLink = String(response?.data?.debug_activation_link || '').trim();
      if (fallbackLink) {
        setInfoMessage(
          "Service email indisponible en local. Utilisez le lien d'activation ci-dessous."
        );
        setDebugActivationLink(fallbackLink);
      } else {
        setInfoMessage(
          `Un lien d'activation a été envoyé à ${recipientLabel}. Vérifiez votre boîte de réception et vos courriers indésirables.`
        );
      }
    } catch (error) {
      const retryAfter = markCooldownFromError(error, setEmailCooldown);
      if (isEmailDeliveryInProgressError(error)) {
        setActivationEmailAccepted(true);
        if (retryAfter <= 0) {
          setEmailCooldown(DEFAULT_COOLDOWN_SECONDS);
        }
        setInfoMessage(
          `Un lien d'activation a déjà été demandé pour ${recipientLabel}. Vérifiez votre boîte de réception ; vous pourrez renvoyer après le délai.`
        );
        setErrorMessage('');
      } else {
        setErrorMessage(parseApiMessage(error, "Impossible d'envoyer l'email."));
      }
    } finally {
      setEmailSending(false);
    }
  };

  const handleResendSms = async () => {
    if (!activationSessionId || smsSending || smsCooldown > 0) return;
    setErrorMessage('');
    setInfoMessage('');
    setDebugActivationLink('');
    setDebugSmsCode('');
    setSmsSending(true);
    try {
      const response = await apiClient.post('/auth/activation/resend-sms', {
        activation_session_id: activationSessionId,
      });
      setSmsCooldown(DEFAULT_COOLDOWN_SECONDS);
      const fallbackCode = String(response?.data?.debug_sms_code || '').trim();
      if (fallbackCode) {
        setInfoMessage(
          'Service SMS indisponible en local. Utilisez le code de secours ci-dessous.'
        );
        setDebugSmsCode(fallbackCode);
      } else {
        setInfoMessage('Code SMS renvoyé.');
      }
    } catch (error) {
      markCooldownFromError(error, setSmsCooldown);
      setErrorMessage(parseApiMessage(error, "Impossible d'envoyer le SMS."));
    } finally {
      setSmsSending(false);
    }
  };

  const handleVerifySms = async () => {
    if (!activationSessionId) return;
    const code = smsCode.trim();
    if (!/^\d{6}$/.test(code)) {
      setErrorMessage('Entrez un code SMS à 6 chiffres.');
      return;
    }
    setErrorMessage('');
    setInfoMessage('');
    setDebugActivationLink('');
    setDebugSmsCode('');
    setLoading(true);
    try {
      const response = await apiClient.post('/auth/activation/verify-sms', {
        activation_session_id: activationSessionId,
        code,
      });
      updateStatus(response?.data?.activation_status);
      setInfoMessage('Téléphone confirmé.');
      setSmsCode('');
    } catch (error) {
      setErrorMessage(parseApiMessage(error, 'Code SMS invalide ou expiré.'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePhone = async () => {
    if (!activationSessionId) return;
    const phone = newPhone.trim();
    if (phone.length < 7) {
      setErrorMessage('Entrez un numéro de téléphone valide.');
      return;
    }
    setErrorMessage('');
    setInfoMessage('');
    setDebugActivationLink('');
    setLoading(true);
    try {
      const response = await apiClient.post('/auth/activation/update-phone', {
        activation_session_id: activationSessionId,
        phone,
      });
      if (response?.data?.masked_phone) {
        setMaskedPhone(response.data.masked_phone);
      }
      const fallbackCode = String(response?.data?.debug_sms_code || '').trim();
      updateStatus(response?.data?.activation_status);
      setShowPhoneEdit(false);
      setNewPhone('');
      setSmsCooldown(DEFAULT_COOLDOWN_SECONDS);
      if (fallbackCode) {
        setInfoMessage(
          'Numéro mis à jour. Service SMS indisponible en local, utilisez le code de secours ci-dessous.'
        );
        setDebugSmsCode(fallbackCode);
      } else {
        setInfoMessage(response?.data?.message || 'Numéro mis à jour.');
      }
    } catch (error) {
      markCooldownFromError(error, setSmsCooldown);
      setErrorMessage(parseApiMessage(error, 'Impossible de mettre à jour le numéro.'));
    } finally {
      setLoading(false);
    }
  };

  const handleFinalize = async () => {
    if (!activationSessionId) return;
    setErrorMessage('');
    setInfoMessage('');
    setLoading(true);
    try {
      const response = await apiClient.post('/auth/activation/finalize', {
        activation_session_id: activationSessionId,
        ...(status.portal_terms_required ? { accept_current_portal_terms: true } : {}),
      });
      updateStatus(response?.data?.activation_status);
      setInfoMessage('Compte activé. Vous pouvez maintenant vous connecter.');
      removePendingActivationBySessionId(activationSessionId);
      if (prefillEmail) {
        removePendingActivationByEmail(prefillEmail);
      }
    } catch (error) {
      setErrorMessage(parseApiMessage(error, 'Activation finale impossible.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!activationSessionId) return;
    setPendingActivationSession({
      email: prefillEmail,
      activation_session_id: activationSessionId,
      masked_email: maskedEmail || null,
      masked_phone: maskedPhone || null,
    });
  }, [activationSessionId, maskedEmail, maskedPhone, prefillEmail]);

  const showPortalTerms = Boolean(
    status.portal_terms_required &&
      status.email_verified &&
      status.phone_verified &&
      !status.is_finalized
  );
  const termsReady =
    termsCatalog.some((doc) => doc.document_type === 'terms_of_service') &&
    termsCatalog.some((doc) => doc.document_type === 'transport_terms');

  useEffect(() => {
    if (!showPortalTerms || !activationSessionId) {
      return undefined;
    }
    let cancelled = false;
    apiClient
      .get(
        `/auth/activation/portal-terms?activation_session_id=${encodeURIComponent(
          activationSessionId
        )}`
      )
      .then((response) => {
        if (!cancelled) {
          setTermsCatalog(Array.isArray(response?.data?.documents) ? response.data.documents : []);
        }
      })
      .catch(() => {
        if (!cancelled) setTermsCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, [showPortalTerms, activationSessionId]);

  const finalizeEnabled = useMemo(
    () =>
      Boolean(
        status.is_complete &&
          !status.is_finalized &&
          (!status.portal_terms_required || (termsAccepted && termsReady))
      ),
    [status, termsAccepted, termsReady]
  );

  const activationSubtitle = useMemo(() => {
    if (status.requires_email && !status.email_verified) {
      if (status.portal_terms_required) {
        return 'Validez votre email. Le téléphone sera vérifié ensuite, avant l’activation du compte.';
      }
      return 'Validez votre email pour activer le compte. Le téléphone, s’il ne l’est pas déjà, se vérifiera une seule fois avant la prochaine réservation.';
    }
    if (status.requires_phone && !status.phone_verified) {
      return 'Validez votre numéro de téléphone. Cette vérification concerne le compte, pas une réservation.';
    }
    if (showPortalTerms) {
      return 'Téléphone confirmé. Lisez et acceptez les conditions pour activer le compte.';
    }
    return 'Votre compte peut être activé.';
  }, [
    showPortalTerms,
    status.email_verified,
    status.phone_verified,
    status.portal_terms_required,
    status.requires_email,
    status.requires_phone,
  ]);

  const handleLoginRedirect = () => {
    navigate('/login', {
      replace: true,
      state: { activated: true, prefillEmail },
    });
  };

  if (status.is_finalized) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.card}>
          <h1 className={styles.title}>Activation terminee</h1>
          <p className={styles.subtitle}>
            Votre compte est actif. Connectez-vous, puis confirmez votre adresse et vos besoins habituels avant la première réservation.
          </p>
          <p className={`${styles.statusBox} ${styles.statusSuccess}`}>
            Compte active avec succes.
          </p>
          <div className={styles.footerActions}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={handleLoginRedirect}
              disabled={loading}
            >
              Se connecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.card}>
        <h1 className={styles.title}>Activation du compte</h1>
        <p className={styles.subtitle}>{activationSubtitle}</p>

        {infoMessage ? <p className={`${styles.statusBox} ${styles.statusInfo}`}>{infoMessage}</p> : null}
        {debugActivationLink ? (
          <p className={`${styles.statusBox} ${styles.statusInfo}`}>
            Lien de secours:{' '}
            <a href={debugActivationLink} className={styles.inlineLink}>
              Ouvrir le lien d'activation
            </a>
          </p>
        ) : null}
        {debugSmsCode ? (
          <p className={`${styles.statusBox} ${styles.statusInfo}`}>
            Code SMS de secours: <strong>{debugSmsCode}</strong>
          </p>
        ) : null}
        {errorMessage ? <p className={`${styles.statusBox} ${styles.statusError}`}>{errorMessage}</p> : null}
        {status.requires_email ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Email</h2>
          <p className={styles.sectionHint}>
            {activationEmailAccepted || status.email_verified
              ? `Un lien d'activation a été envoyé à ${recipientLabel}.`
              : `Un lien sera envoyé à ${recipientLabel}.`}
          </p>
          {(activationEmailAccepted || status.email_verified) && !status.email_verified ? (
            <p className={styles.sectionHint}>
              Vérifiez votre boîte de réception et vos courriers indésirables.
            </p>
          ) : null}
          {emailSending ? (
            <p className={styles.sectionHint}>Envoi de l&apos;e-mail…</p>
          ) : null}
          <span className={`${styles.badge} ${status.email_verified ? styles.badgeDone : styles.badgePending}`}>
            {status.email_verified ? 'Email confirmé' : 'En attente de confirmation'}
          </span>
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonSecondary}`}
              onClick={handleResendEmail}
              disabled={
                loading ||
                emailSending ||
                status.email_verified ||
                emailCooldown > 0 ||
                !activationSessionId
              }
            >
              {emailSending
                ? "Envoi…"
                : emailCooldown > 0
                  ? `Renvoyer dans ${emailCooldown}s`
                  : "Renvoyer l'email"}
            </button>
          </div>
        </section>
        ) : null}

        {status.requires_phone ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>SMS</h2>
          <p className={styles.sectionHint}>
            Saisissez le code reçu sur {maskedPhone || 'votre téléphone'}.
          </p>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => {
              setShowPhoneEdit((prev) => !prev);
              setErrorMessage('');
              setInfoMessage('');
            }}
            disabled={loading || status.phone_verified}
          >
            {showPhoneEdit ? 'Annuler' : 'Mauvais numéro ?'}
          </button>
          {showPhoneEdit ? (
            <div className={styles.row}>
              <input
                type="text"
                inputMode="tel"
                className={styles.input}
                placeholder="Nouveau numéro (+41...)"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                disabled={loading || status.phone_verified}
              />
              <button
                type="button"
                className={`${styles.button} ${styles.buttonSecondary}`}
                onClick={handleUpdatePhone}
                disabled={loading || status.phone_verified || !activationSessionId}
              >
                Mettre à jour
              </button>
            </div>
          ) : null}
          <span className={`${styles.badge} ${status.phone_verified ? styles.badgeDone : styles.badgePending}`}>
            {status.phone_verified ? 'Téléphone confirmé' : 'En attente de confirmation'}
          </span>
          <div className={styles.row}>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              className={styles.input}
              placeholder="Code à 6 chiffres"
              value={smsCode}
              onChange={(e) => setSmsCode(e.target.value.replace(/[^\d]/g, ''))}
              disabled={loading || status.phone_verified}
            />
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={handleVerifySms}
              disabled={loading || status.phone_verified || !activationSessionId}
            >
              Valider le code
            </button>
          </div>
          <div className={styles.row}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonSecondary}`}
              onClick={handleResendSms}
              disabled={
                loading ||
                smsSending ||
                status.phone_verified ||
                smsCooldown > 0 ||
                !activationSessionId
              }
            >
              {smsSending
                ? 'Envoi…'
                : smsCooldown > 0
                  ? `Renvoyer dans ${smsCooldown}s`
                  : 'Renvoyer le code'}
            </button>
          </div>
        </section>
        ) : null}

        {showPortalTerms ? (
          <section className={styles.section} aria-labelledby="portal-terms-title">
            <h2 id="portal-terms-title" className={styles.sectionTitle}>
              Conditions
            </h2>
            <p className={styles.sectionHint}>
              Ces textes sont ceux du catalogue serveur. Les lire fait partie de l&apos;activation.
            </p>
            <div className={styles.termsLinks}>
              {termsCatalog.map((doc) => (
                <button
                  key={doc.document_type}
                  type="button"
                  className={styles.linkButton}
                  onClick={() => setOpenTermsDoc(doc)}
                >
                  {doc.document_type === 'transport_terms'
                    ? 'Conditions générales de transport'
                    : "Conditions générales d'utilisation"}
                  {doc.terms_version ? ` ${doc.terms_version}` : ''}
                </button>
              ))}
            </div>
            {openTermsDoc ? (
              <pre className={styles.termsBody}>{openTermsDoc.canonical_body}</pre>
            ) : null}
            <label className={styles.termsAccept}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(event) => setTermsAccepted(event.target.checked)}
              />
              <span>
                J&apos;ai lu et j&apos;accepte les Conditions générales d&apos;utilisation et les
                Conditions générales de transport.
              </span>
            </label>
          </section>
        ) : null}

        <div className={styles.footerActions}>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonPrimary}`}
            onClick={handleFinalize}
            disabled={!finalizeEnabled || loading}
          >
            Activer mon compte
          </button>
        </div>

        {loading && !emailSending && !smsSending ? (
          <p className={styles.subtitle}>Traitement en cours...</p>
        ) : null}
      </div>
    </div>
  );
};

export default SignupActivation;
